import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { motion, AnimatePresence } from 'framer-motion';
import DreamModal from '../components/DreamModal';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

// Utility function to check if device is mobile
const isMobile = () => window.innerWidth <= 768;

// Star component with mobile optimization
const Star = ({ size, delay, duration }: { size: number; delay: number; duration: number }) => (
  <motion.div
    className="absolute rounded-full bg-dream-200"
    style={{
      width: size,
      height: size,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
    }}
    initial={{ opacity: 0 }}
    animate={{
      opacity: [0, 0.8, 0],
    }}
    transition={{
      duration: duration * (isMobile() ? 1.5 : 1),
      delay: delay,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "linear",
    }}
  />
);

// Floating orb effect with mobile optimization
const DreamOrb = ({ delay }: { delay: number }) => (
  <motion.div
    className="absolute rounded-full bg-gradient-to-b from-dream-400/10 to-dream-500/10 blur-xl"
    style={{
      width: Math.random() * (isMobile() ? 50 : 100) + 50,
      height: Math.random() * (isMobile() ? 50 : 100) + 50,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
    }}
    initial={{ opacity: 0 }}
    animate={{
      opacity: [0.3, 0.6, 0.3],
      y: [0, -20, 0],
    }}
    transition={{
      duration: Math.random() * 3 + 5,
      delay: delay,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "linear",
    }}
  />
);

interface Dream {
  id: string;
  title: string;
  content: string;
  tags: string[];
  isPublic: boolean;
  userId: string;
  authorName: string;
  createdAt: any;
  updatedAt: any;
}

interface User {
  id: string;
  displayName: string;
  photoURL: string | null;
  icon: string;
  description: string;
  dreamCount: number;
}

const SearchPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Dream[]>([]);
  const [userResults, setUserResults] = useState<User[]>([]);
  const [selectedDream, setSelectedDream] = useState<Dream | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'recent' | 'popular' | 'users'>('all');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isMobileView, setIsMobileView] = useState(isMobile());

  const performSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setUserResults([]);
      return;
    }

    setIsLoading(true);
    try {
      // Search dreams
      const dreamsRef = collection(db, 'dreams');
      const searchTerms = searchQuery.toLowerCase().split(' ');
      
      const dreamQuery = query(
        dreamsRef,
        where('isPublic', '==', true),
        orderBy('createdAt', 'desc')
      );

      const dreamSnapshot = await getDocs(dreamQuery);
      let dreamResults = dreamSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Dream));

      // Filter dreams based on search terms
      dreamResults = dreamResults.filter(dream => {
        const matchesTitle = dream.title.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesContent = dream.content.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesTags = dream.tags.some(tag => 
          searchTerms.some(term => tag.toLowerCase().includes(term))
        );
        const matchesAuthor = dream.authorName.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesTitle || matchesContent || matchesTags || matchesAuthor;
      });

      // Search users if the filter is 'users' or 'all'
      if (activeFilter === 'users' || activeFilter === 'all') {
        const usersRef = collection(db, 'users');
        const userSnapshot = await getDocs(usersRef);
        let userResults = userSnapshot.docs
          .map(doc => {
            const userData = doc.data();
            return {
              id: doc.id,
              displayName: userData.displayName || '',
              photoURL: userData.photoURL || null,
              icon: userData.icon || '🌙',
              description: userData.description || 'A mysterious dreamer...',
              dreamCount: dreamResults.filter(dream => dream.userId === doc.id).length
            } as User;
          })
          .filter(user => 
            user.displayName.toLowerCase().includes(searchQuery.toLowerCase())
          );
        setUserResults(userResults);
      } else {
        setUserResults([]);
      }

      // Apply filters to dreams
      switch (activeFilter) {
        case 'recent':
          dreamResults.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
          break;
        case 'popular':
          // You could add a view count or likes field to implement this
          break;
        case 'users':
          dreamResults = [];
          break;
        default:
          break;
      }

      setSearchResults(dreamResults);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      performSearch();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, activeFilter]);

  // Add resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-dream-950 via-dream-900 to-dream-950 overflow-hidden relative">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-dream-950/80 via-transparent to-dream-950/80"></div>
        
        {/* Generate stars */}
        {Array.from({ length: isMobileView ? 15 : 30 }).map((_, i) => (
          <Star
            key={`star-${i}`}
            size={Math.random() * 2 + 1}
            delay={Math.random() * 5}
            duration={Math.random() * 3 + 2}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative min-h-screen pt-20 px-4 sm:px-6 lg:px-8">
        {/* Search Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto mb-8"
        >
          <div className="relative group">
            <motion.div
              className="absolute -inset-1 bg-gradient-to-r from-dream-500/20 to-dream-400/20 rounded-xl blur-xl
                       opacity-0 group-hover:opacity-100 transition-all duration-500"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search dreams, users, or tags..."
              className="w-full px-6 py-4 bg-dream-900/50 border border-dream-700/30 rounded-xl
                       text-dream-100 placeholder-dream-400 focus:outline-none focus:ring-2
                       focus:ring-dream-500/50 focus:border-dream-500/50 transition-all
                       backdrop-blur-sm relative z-10"
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-dream-400 z-20">
              {isLoading ? (
                <motion.div 
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="rounded-full h-5 w-5 border-t-2 border-b-2 border-dream-400"
                />
              ) : (
                <motion.span 
                  className="text-xl inline-block"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  🔍
                </motion.span>
              )}
            </div>
          </div>

          {/* Filters */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex gap-2 mt-4 overflow-x-auto pb-2"
          >
            {(['all', 'recent', 'popular', 'users'] as const).map((filter, index) => (
              <motion.button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap
                  ${activeFilter === filter
                    ? 'bg-dream-700/50 text-dream-100 border-dream-500/50'
                    : 'bg-dream-800/30 text-dream-400 hover:bg-dream-700/30 hover:text-dream-300'
                  } border border-dream-700/30 backdrop-blur-sm`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </motion.button>
            ))}
          </motion.div>
        </motion.div>

        {/* Search Results */}
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            {/* User Results */}
            {userResults.length > 0 && (activeFilter === 'all' || activeFilter === 'users') && (
              <motion.div
                key="user-results"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="mb-8"
              >
                <motion.h2 
                  className="text-dream-100 text-xl font-medium mb-4 relative inline-block"
                  whileHover={{ scale: 1.02 }}
                >
                  Users
                  <motion.div
                    className="absolute -inset-2 bg-dream-500/10 rounded-lg blur-xl"
                    animate={{
                      opacity: [0.2, 0.4, 0.2],
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                  />
                </motion.h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {userResults.map((user, index) => (
                    <motion.div
                      key={`user-${user.id}`}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ 
                        opacity: 1, 
                        scale: 1,
                        transition: {
                          duration: 0.5,
                          delay: index * 0.1
                        }
                      }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      whileHover={{ scale: 1.02, y: -5 }}
                      className="bg-dream-900/50 border border-dream-700/30 rounded-xl p-6
                               backdrop-blur-sm hover:bg-dream-800/50 transition-all
                               hover:border-dream-500/50 cursor-pointer group relative"
                      onClick={() => navigate(`/profile/${user.id}`)}
                    >
                      <motion.div
                        className="absolute -inset-2 bg-gradient-to-r from-dream-500/10 to-dream-400/10 
                                 rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"
                      />
                      <div className="flex items-center gap-4 relative z-10">
                        <motion.div 
                          className="w-12 h-12 rounded-full bg-dream-800 flex items-center justify-center
                                    text-2xl border-2 border-dream-700/50 relative overflow-hidden"
                          whileHover={{ scale: 1.1 }}
                        >
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-b from-dream-500/20 to-transparent"
                            animate={{
                              opacity: [0.3, 0.6, 0.3],
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              repeatType: "reverse",
                            }}
                          />
                          {user.icon}
                        </motion.div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-dream-100 font-medium group-hover:text-dream-50 truncate">
                            {user.displayName}
                          </h3>
                          <p className="text-dream-400 text-sm line-clamp-2">
                            {user.description}
                          </p>
                          <p className="text-dream-500 text-sm mt-1">
                            {user.dreamCount} dreams shared
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Dream Results */}
            {searchResults.length > 0 && activeFilter !== 'users' && (
              <motion.div
                key="dream-results"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2"
              >
                {searchResults.map((dream, index) => (
                  <motion.div
                    key={dream.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ 
                      opacity: 1, 
                      scale: 1,
                      transition: {
                        duration: 0.5,
                        delay: index * 0.1
                      }
                    }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    whileHover={{ scale: 1.02, y: -5 }}
                    className="bg-dream-900/50 border border-dream-700/30 rounded-xl p-6
                             backdrop-blur-sm hover:bg-dream-800/50 transition-all
                             hover:border-dream-500/50 cursor-pointer group relative"
                    onClick={() => setSelectedDream(dream)}
                  >
                    <motion.div
                      className="absolute -inset-2 bg-gradient-to-r from-dream-500/10 to-dream-400/10 
                               rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"
                    />
                    <div className="relative z-10">
                      <h3 className="text-dream-100 font-medium text-lg mb-2 group-hover:text-dream-50">
                        {dream.title}
                      </h3>
                      <p className="text-dream-400 text-sm mb-4 line-clamp-2">
                        {dream.content}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {dream.tags.map((tag, tagIndex) => (
                          <motion.span
                            key={tagIndex}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ 
                              opacity: 1, 
                              scale: 1,
                              transition: {
                                duration: 0.3,
                                delay: tagIndex * 0.1
                              }
                            }}
                            className="px-2 py-1 text-xs rounded-full bg-dream-800/50
                                     text-dream-300 border border-dream-700/30"
                          >
                            {tag}
                          </motion.span>
                        ))}
                      </div>
                      <div className="flex items-center text-dream-400 text-sm">
                        <span>By {dream.authorName}</span>
                        <span className="mx-2">•</span>
                        <span>{new Date(dream.createdAt.toDate()).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}

            {/* No Results Message */}
            {searchQuery && !isLoading && searchResults.length === 0 && userResults.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="text-center text-dream-400 mt-8"
              >
                No dreams found in the dreamscape...
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Dream Modal */}
        <AnimatePresence>
          {selectedDream && (
            <div className="fixed inset-0 z-50 overflow-hidden">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/70 backdrop-blur"
                onClick={() => setSelectedDream(null)}
              />
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="relative bg-dream-900 rounded-xl shadow-xl w-full max-w-2xl
                             overflow-hidden border border-dream-700/50"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <DreamModal
                      memory={{
                        ...selectedDream,
                        content: selectedDream.content || '',
                      }}
                      onClose={() => setSelectedDream(null)}
                    />
                  </motion.div>
                </div>
              </div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SearchPage; 